import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useItems, useDeleteItem, useBulkDeleteItems, useUserViews, useCreateView, useUpdateView, useDeleteView, useSetDefaultView, useTasks, useApprovals, useActiveUsers, useCustomFields, useWorkflowStages, useAllChoices, useRiskMatrix } from '../hooks/useQueries';
import { useIsMobile } from '../hooks/useIsMobile';
import { useHasRole } from '../hooks/useHasRole';
import { useColumnWidths } from '../hooks/useColumnWidths';
import useAuthenticatedFetch from '../hooks/useAuthenticatedFetch';
import ListViewPage from '../components/Layout/ListViewPage';
import Modal from '../components/Popup/Modal';
import BulkActionsPopup from '../components/Popup/BulkActionsPopup';
import ExportModal from '../components/Layout/ExportModal';
import { formatDate } from '../utils/dateUtils';
import { exportToExcel, exportToCSV } from '../utils/exportUtils';
import {
  evaluateItemFilters,
  normalizeCustomFieldKey as normalizeCustomFieldKeyUtil
} from '../utils/customFieldUtils';
import { formatItemId, formatItemRef, matchesItemId } from '../utils/itemIdFormat';
import API_CONFIG from '../config/apiConfig';
import './items.css';

// localStorage utilities for table state persistence
const ITEMS_TABLE_STATE_KEY = 'items-table-state';

const saveTableState = (state) => {
  try {
    localStorage.setItem(ITEMS_TABLE_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save table state:', error);
  }
};

const loadTableState = () => {
  try {
    const saved = localStorage.getItem(ITEMS_TABLE_STATE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch (error) {
    console.warn('Failed to load table state:', error);
    return {};
  }
};

// In-memory view selection for this runtime only (resets on full page refresh).
let itemsRuntimeViewSelection = null;

// Saved views store the stage quick filter under the column key the view config
// modal derives from the dropdown ("current_stage_name"); accept "stage" too.
const stageFilterFromView = (filters) => filters?.current_stage_name || filters?.stage || '';

const normalizeMultiFilter = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value) return [value];
  return [];
};

// items.owner is free text and has held both emails and display names over time,
// so owner comparisons are normalised before matching.
const normalizeOwnerText = (value) => String(value ?? '').trim().toLowerCase();

// The owner filter a view carries. Seeded "My Items" views exist in both shapes:
// older rows store { owner: "<email>" }, newer ones store { owner_user_id: <id> }.
// The raw value is kept as-is and only resolved to a user at filter time, when the
// user list is loaded.
const viewOwnerFilterFrom = (filters) => {
  if (filters?.owner_user_id) return { userId: filters.owner_user_id };
  if (filters?.owner) return { text: filters.owner };
  return null;
};

export default function ItemsListView() {
  const navigate = useNavigate();
  const location = useLocation();
  const authenticatedFetch = useAuthenticatedFetch();
  const isMobile = useIsMobile();

  // Viewers can read items but not create them, so the entry points that lead to
  // a create are hidden. The server enforces this too - see requireUser() on the
  // item create routes.
  const { hasRole: canCreateItems } = useHasRole('user');
  
  // Check if we're on the /my-items route
  const isMyItemsRoute = location.pathname === '/my-items';
  
  // Load initial state from localStorage
  const savedState = useMemo(() => loadTableState(), []);
  const [searchTerm, setSearchTerm] = useState(savedState.searchTerm || '');
  const [sortConfig, setSortConfig] = useState(savedState.sortConfig || { key: 'created_on', direction: 'desc' });
  const [stageFilter, setStageFilter] = useState(() => normalizeMultiFilter(savedState.stageFilter));
  // Secondary filters, surfaced through the "More filters" popover
  const [ownerFilter, setOwnerFilter] = useState(() => normalizeMultiFilter(savedState.ownerFilter));
  // Owner filter carried by the selected view (this is what makes "My Items" show
  // only your items). Separate from ownerFilter above, which is the manual dropdown.
  const [viewOwnerFilter, setViewOwnerFilter] = useState(null);
  const [typeFilter, setTypeFilter] = useState(() => normalizeMultiFilter(savedState.typeFilter));
  const [primaryOwnerFilter, setPrimaryOwnerFilter] = useState(() => normalizeMultiFilter(savedState.primaryOwnerFilter));
  const [secondaryOwnerFilter, setSecondaryOwnerFilter] = useState(() => normalizeMultiFilter(savedState.secondaryOwnerFilter));
  const [riskZoneFilter, setRiskZoneFilter] = useState(() => normalizeMultiFilter(savedState.riskZoneFilter));
  const [projectFilter, setProjectFilter] = useState(() => normalizeMultiFilter(savedState.projectFilter));
  const [categoryFilter, setCategoryFilter] = useState(() => normalizeMultiFilter(savedState.categoryFilter));
  const [expandedChildItems, setExpandedChildItems] = useState(() => {
    if (savedState.expandedChildItems && Array.isArray(savedState.expandedChildItems)) {
      return new Set(savedState.expandedChildItems);
    }
    return new Set();
  });
  const [selectedRows, setSelectedRows] = useState(() => new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isBulkExporting, setIsBulkExporting] = useState(false);
  const [itemStatusFilter, setItemStatusFilter] = useState(() => (isMyItemsRoute ? ['open'] : ['open', 'closed']));
  const [showExportModal, setShowExportModal] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [deleteModal, setDeleteModal] = useState({ show: false, item: null });
  const [bulkDeleteModal, setBulkDeleteModal] = useState({ show: false, selectedRows: new Set() });
  const [deleteViewModal, setDeleteViewModal] = useState({ show: false, view: null });
  
  // Task hover popup state
  const [taskHoverPopup, setTaskHoverPopup] = useState({
    show: false,
    itemId: null,
    position: { x: 0, y: 0, anchorBottom: 0 },
    tasks: [],
    expanded: false,
    closing: false
  });
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [hideTimeout, setHideTimeout] = useState(null);
  // Resolved vertical placement of the task popup (flips above the row near the viewport bottom)
  const taskPopupRef = useRef(null);
  const [taskPopupPlacement, setTaskPopupPlacement] = useState({ top: null, flipped: false });
  
  // View management state
  const [currentView, setCurrentView] = useState('default');
  const [viewConfig, setViewConfig] = useState(null); // eslint-disable-line no-unused-vars
  const [visibleColumns, setVisibleColumns] = useState(
    savedState.visibleColumns || ['id', 'current_stage_order', 'title', 'description', 'category_name', 'project_id', 'owner', 'created_on']
  );
  const [customFilters, setCustomFilters] = useState([]);
  
  // Track if we've done initial view restoration
  const hasRestoredView = useRef(false);
  const isViewChangeInProgress = useRef(false);

  // React Query hooks - fetch open and closed items separately
  const { data: openItems = [], isLoading: loadingOpen, error: errorOpen } = useItems({ status: 'open' });
  const { data: closedItems = [], isLoading: loadingClosed, error: errorClosed } = useItems({ status: 'closed' });
  const { data: allTasks = [] } = useTasks();
  const { data: activeUsers = [] } = useActiveUsers();
  const { data: exportCustomFields = [] } = useCustomFields(null, true);
  const { data: workflowStages = [] } = useWorkflowStages();
  const { data: allChoices = {} } = useAllChoices();
  const { data: riskMatrixConfig } = useRiskMatrix();
  
  // Fetch pending approvals to show approval badges
  const { data: pendingApprovals = [] } = useApprovals({ status: 'requested' });
  
  // Combine loading and error states
  const loading = loadingOpen || loadingClosed;
  const error = errorOpen || errorClosed;
  const deleteItemMutation = useDeleteItem();
  const bulkDeleteItemsMutation = useBulkDeleteItems();

  // User views hooks
  const { data: userViews = [], error: userViewsError } = useUserViews('items'); // eslint-disable-line no-unused-vars
  const createViewMutation = useCreateView();
  const updateViewMutation = useUpdateView();
  const deleteViewMutation = useDeleteView();
  const setDefaultViewMutation = useSetDefaultView();

  // Column widths are saved per view (and mirrored to localStorage so the
  // built-in "All Items" view remembers them too).
  const { columnWidths, handleColumnResize } = useColumnWidths({
    tableName: 'items',
    currentView,
    userViews
  });


  // Risk zone buckets come from the configured matrix thresholds. items.risk_zone
  // stores the numeric risk score, so filtering means testing the score against
  // each zone's [min, max] range rather than comparing strings.
  const riskZoneBuckets = useMemo(() => {
    let thresholds = riskMatrixConfig?.zone_thresholds;

    // Older rows can come back as a JSON string rather than an object.
    if (typeof thresholds === 'string') {
      try {
        thresholds = JSON.parse(thresholds);
      } catch (error) {
        console.warn('Failed to parse zone_thresholds:', error);
        thresholds = null;
      }
    }

    if (!thresholds || typeof thresholds !== 'object') return [];

    return Object.entries(thresholds)
      .filter(([, range]) => range && Number.isFinite(Number(range.min)) && Number.isFinite(Number(range.max)))
      .map(([name, range]) => ({
        name,
        min: Number(range.min),
        max: Number(range.max)
      }))
      .sort((a, b) => a.min - b.min);
  }, [riskMatrixConfig]);

  const getRiskZoneName = useCallback((rawScore) => {
    if (rawScore === null || rawScore === undefined || rawScore === '') return null;

    const score = Number(rawScore);
    if (Number.isFinite(score)) {
      const bucket = riskZoneBuckets.find(zone => score >= zone.min && score <= zone.max);
      return bucket ? bucket.name : null;
    }

    // Defensive: a few legacy rows may hold the zone name instead of the score.
    const asText = String(rawScore).trim().toLowerCase();
    const named = riskZoneBuckets.find(zone => zone.name.toLowerCase() === asText);
    return named ? named.name : null;
  }, [riskZoneBuckets]);

  // Custom filtering logic
  const ownerDisplayLookup = useMemo(() => {
    const byId = new Map();
    const byEmail = new Map();
    const byName = new Map();

    if (Array.isArray(activeUsers)) {
      activeUsers.forEach(u => {
        if (!u) return;
        if (u.id != null) byId.set(String(u.id), u);
        if (u.email) byEmail.set(String(u.email).trim().toLowerCase(), u);
        if (u.name) byName.set(String(u.name).trim().toLowerCase(), u);
      });
    }

    return { byId, byEmail, byName };
  }, [activeUsers]);

  const getOwnerDisplay = useCallback((item) => {
    const rawOwner = item?.owner;
    const rawOwnerUserId = item?.owner_user_id;

    if (rawOwnerUserId != null) {
      const match = ownerDisplayLookup.byId.get(String(rawOwnerUserId));
      if (match?.name) return match.name;
    }

    const ownerText = String(rawOwner ?? '').trim();
    if (!ownerText) return '-';

    const ownerLower = ownerText.toLowerCase();

    // If stored as email, show the user's friendly name when possible
    if (ownerLower.includes('@')) {
      const match = ownerDisplayLookup.byEmail.get(ownerLower);
      if (match?.name) return match.name;

      // Best-effort fallback: derive name from local part
      const localPart = ownerText.split('@')[0] || ownerText;
      const cleaned = localPart.replace(/[._-]+/g, ' ').trim();
      if (!cleaned) return ownerText;
      return cleaned
        .split(' ')
        .filter(Boolean)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
    }

    // If stored as name, normalize capitalization using directory when possible
    const matchByName = ownerDisplayLookup.byName.get(ownerLower);
    if (matchByName?.name) return matchByName.name;

    return ownerText;
  }, [ownerDisplayLookup]);

  const customFilter = useCallback((data) => {
    const searchValue = String(searchTerm || '').trim().toLowerCase();

    // Apply all non-search filters first so search expansion (parent -> child)
    // only includes rows that already satisfy active dropdown/custom filters.
    let filtered = data;

    // Apply stage filter
    if (stageFilter.length > 0) {
      const allowedStages = new Set(stageFilter);
      filtered = filtered.filter(item => allowedStages.has(item.current_stage_name));
    }

    // Owner filter carried by the selected view - this is what makes "My Items" show
    // only your items. Items predate owner_user_id, so an item is matched by its id
    // when it has one and by its owner text when it does not.
    if (viewOwnerFilter) {
      const targetUser = viewOwnerFilter.userId != null
        ? ownerDisplayLookup.byId.get(String(viewOwnerFilter.userId))
        : (ownerDisplayLookup.byEmail.get(normalizeOwnerText(viewOwnerFilter.text))
          || ownerDisplayLookup.byName.get(normalizeOwnerText(viewOwnerFilter.text)));

      const targetId = viewOwnerFilter.userId ?? targetUser?.id ?? null;
      const targetTexts = new Set(
        [viewOwnerFilter.text, targetUser?.email, targetUser?.name]
          .filter(Boolean)
          .map(normalizeOwnerText)
      );

      filtered = filtered.filter(item => {
        if (item.owner_user_id != null && targetId != null) {
          return String(item.owner_user_id) === String(targetId);
        }
        const ownerText = normalizeOwnerText(item.owner);
        return ownerText !== '' && targetTexts.has(ownerText);
      });
    }

    // Additional filters (More filters popover). Skipped while a view's owner filter
    // is active, since that one is already narrower.
    if (ownerFilter.length > 0 && !viewOwnerFilter) {
      const allowedOwners = new Set(ownerFilter);
      filtered = filtered.filter(item => allowedOwners.has(item.owner));
    }

    if (typeFilter.length > 0) {
      const allowedTypes = new Set(typeFilter);
      filtered = filtered.filter(item => allowedTypes.has(item.type));
    }

    if (primaryOwnerFilter.length > 0) {
      const allowedPrimaryOwners = new Set(primaryOwnerFilter);
      filtered = filtered.filter(item => allowedPrimaryOwners.has(item.primary_owner));
    }

    if (secondaryOwnerFilter.length > 0) {
      const allowedSecondaryOwners = new Set(secondaryOwnerFilter);
      filtered = filtered.filter(item => allowedSecondaryOwners.has(item.secondary_owner));
    }

    if (riskZoneFilter.length > 0) {
      const allowedRiskZones = new Set(riskZoneFilter);
      filtered = filtered.filter(item => allowedRiskZones.has(getRiskZoneName(item.risk_zone)));
    }

    // Apply project filter
    if (projectFilter.length > 0) {
      const allowedProjects = new Set(projectFilter);
      filtered = filtered.filter(item => allowedProjects.has(item.project_name));
    }

    // Apply category filter
    if (categoryFilter.length > 0) {
      const allowedCategories = new Set(categoryFilter);
      filtered = filtered.filter(item => allowedCategories.has(item.category_name));
    }

    // Apply custom filters (AND/OR per row; AND binds tighter than OR)
    if (Array.isArray(customFilters) && customFilters.length > 0) {
      filtered = filtered.filter(item => evaluateItemFilters(item, customFilters));
    }

    if (!searchValue) {
      return filtered;
    }

    const directMatches = filtered.filter(item =>
      item.title?.toLowerCase().includes(searchValue) ||
      item.description?.toLowerCase().includes(searchValue) ||
      getOwnerDisplay(item)?.toLowerCase().includes(searchValue) ||
      item.category_name?.toLowerCase().includes(searchValue) ||
      item.project_name?.toLowerCase().includes(searchValue) ||
      item.current_stage_name?.toLowerCase().includes(searchValue) ||
      matchesItemId(item.id, searchTerm)
    );

    const visibleIds = new Set(directMatches.map(item => item.id));
    const parentToChildren = new Map();

    filtered.forEach(item => {
      if (!item.child_of) return;
      const siblings = parentToChildren.get(item.child_of) || [];
      siblings.push(item.id);
      parentToChildren.set(item.child_of, siblings);
    });

    // Include all descendants of matched parents (not just one level deep).
    const queue = Array.from(visibleIds);
    while (queue.length > 0) {
      const parentId = queue.shift();
      const childIds = parentToChildren.get(parentId) || [];
      childIds.forEach(childId => {
        if (visibleIds.has(childId)) return;
        visibleIds.add(childId);
        queue.push(childId);
      });
    }

    return filtered.filter(item => visibleIds.has(item.id));
  }, [
    searchTerm,
    stageFilter,
    projectFilter,
    categoryFilter,
    ownerFilter,
    viewOwnerFilter,
    ownerDisplayLookup,
    typeFilter,
    primaryOwnerFilter,
    secondaryOwnerFilter,
    riskZoneFilter,
    getRiskZoneName,
    customFilters,
    getOwnerDisplay
  ]);

  // Calculate task counts for open items
  const itemsWithTaskCounts = useMemo(() => {
    const taskCounts = {};
    allTasks.forEach(task => {
      if (task.parent_id) {
        taskCounts[task.parent_id] = (taskCounts[task.parent_id] || 0) + 1;
      }
    });
    
    const itemsWithCounts = openItems.map(item => ({
      ...item,
      task_count: taskCounts[item.id] || 0
    }));
    
    // Apply custom filters to the items
    return customFilter(itemsWithCounts);
  }, [openItems, allTasks, customFilter]);

  // Calculate task counts for closed items
  const closedItemsWithTaskCounts = useMemo(() => {
    const taskCounts = {};
    allTasks.forEach(task => {
      if (task.parent_id) {
        taskCounts[task.parent_id] = (taskCounts[task.parent_id] || 0) + 1;
      }
    });
    
    const itemsWithCounts = closedItems.map(item => ({
      ...item,
      task_count: taskCounts[item.id] || 0
    }));
    
    // Apply custom filters to the closed items
    return customFilter(itemsWithCounts);
  }, [closedItems, allTasks, customFilter]);

  // Process hierarchical relationships for open items
  const hierarchicalOpenItems = useMemo(() => {
    // First, apply any sorting to the base items
    const sortedItems = [...itemsWithTaskCounts].sort((a, b) => {
      if (sortConfig.key && sortConfig.direction) {
        const aVal = a[sortConfig.key] || '';
        const bVal = b[sortConfig.key] || '';
        
        if (sortConfig.direction === 'asc') {
          return aVal.toString().localeCompare(bVal.toString(), undefined, { numeric: true });
        } else {
          return bVal.toString().localeCompare(aVal.toString(), undefined, { numeric: true });
        }
      }
      return 0;
    });
    
    const itemsMap = new Map();
    const parentItems = [];
    const childItems = [];

    // Categorize items and create map
    sortedItems.forEach(item => {
      itemsMap.set(item.id, { ...item, children: [] });
      
      if (item.child_of) {
        childItems.push(item);
      } else {
        parentItems.push(item);
      }
    });

    // Associate children with their parents
    childItems.forEach(childItem => {
      const parentId = childItem.child_of; // Single parent ID
      const parentItem = itemsMap.get(parentId);
      if (parentItem) {
        parentItem.children.push(childItem);
      } else {
        // Parent not found, treat as orphaned child (show in root level)
        parentItems.push(childItem);
      }
    });

    // A child belongs under its parent whatever its own status, so closed
    // children of an open parent are pulled in here. Without this they are
    // absent from this view entirely and surface in the closed list as
    // rootless orphans, detached from the parent they belong to.
    closedItemsWithTaskCounts.forEach(closedItem => {
      if (!closedItem.child_of) return;
      const parentItem = itemsMap.get(closedItem.child_of);
      if (parentItem) {
        parentItem.children.push(closedItem);
      }
    });

    // Update task counts for parent items to include child task counts
    const result = parentItems.map(item => {
      const mappedItem = itemsMap.get(item.id) || item;
      if (mappedItem.children && mappedItem.children.length > 0) {
        // Calculate total task count including child tasks
        const childTaskCount = mappedItem.children.reduce((sum, child) => sum + (child.task_count || 0), 0);
        mappedItem.total_task_count = (mappedItem.task_count || 0) + childTaskCount;
      } else {
        mappedItem.total_task_count = mappedItem.task_count || 0;
      }
      return mappedItem;
    });

    return result;
  }, [itemsWithTaskCounts, closedItemsWithTaskCounts, sortConfig]);

  // Process hierarchical relationships for closed items
  const hierarchicalClosedItems = useMemo(() => {
    // First, apply any sorting to the base items
    const sortedItems = [...closedItemsWithTaskCounts].sort((a, b) => {
      if (sortConfig.key && sortConfig.direction) {
        const aVal = a[sortConfig.key] || '';
        const bVal = b[sortConfig.key] || '';
        
        if (sortConfig.direction === 'asc') {
          return aVal.toString().localeCompare(bVal.toString(), undefined, { numeric: true });
        } else {
          return bVal.toString().localeCompare(aVal.toString(), undefined, { numeric: true });
        }
      }
      return 0;
    });
    
    const itemsMap = new Map();
    const parentItems = [];
    const childItems = [];

    // Categorize items and create map
    sortedItems.forEach(item => {
      itemsMap.set(item.id, { ...item, children: [] });
      
      if (item.child_of) {
        childItems.push(item);
      } else {
        parentItems.push(item);
      }
    });

    // Associate children with their parents
    childItems.forEach(childItem => {
      const parentId = childItem.child_of; // Single parent ID
      const parentItem = itemsMap.get(parentId);
      if (parentItem) {
        parentItem.children.push(childItem);
      } else {
        // Parent not found, treat as orphaned child (show in root level)
        parentItems.push(childItem);
      }
    });

    return parentItems.map(item => itemsMap.get(item.id) || item);
  }, [closedItemsWithTaskCounts, sortConfig]);

  const parentIdsWithChildren = useMemo(() => {
    const ids = new Set();

    const collectIds = (items) => {
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          ids.add(item.id);
          collectIds(item.children);
        }
      });
    };

    collectIds(hierarchicalOpenItems);
    collectIds(hierarchicalClosedItems);

    return ids;
  }, [hierarchicalOpenItems, hierarchicalClosedItems]);

  // Flatten hierarchical items for filter dropdowns
  const allItemsData = useMemo(() => [...itemsWithTaskCounts, ...closedItemsWithTaskCounts], [itemsWithTaskCounts, closedItemsWithTaskCounts]);

  const formatExportLabel = useCallback((key) => {
    if (!key) return '';
    const normalized = String(key)
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }, []);

  const normalizeCustomFieldKey = useCallback((value) => normalizeCustomFieldKeyUtil(value), []);

  const scoredCustomFieldMeta = useMemo(() => {
    const meta = new Map();
    (Array.isArray(exportCustomFields) ? exportCustomFields : []).forEach((field) => {
      if (!field) return;
      const normalizedKey = normalizeCustomFieldKey(field.name || field.label);
      if (!normalizedKey) return;

      const nameLower = normalizeCustomFieldKey(field.name || '');
      const labelLower = normalizeCustomFieldKey(field.label || '');
      const options = Array.isArray(field.options) ? field.options : [];
      const optionScoreMap = new Map();

      options.forEach((option) => {
        if (!option) return;
        const score = Number(option.score);
        if (!Number.isFinite(score)) return;

        const optionValue = normalizeCustomFieldKey(option.option_value);
        if (optionValue) optionScoreMap.set(optionValue, score);

        const optionLabel = normalizeCustomFieldKey(option.option_label);
        if (optionLabel) optionScoreMap.set(optionLabel, score);
      });

      const isScoredField =
        nameLower === 'probability' ||
        nameLower === 'velocity' ||
        nameLower.includes('impact') ||
        labelLower.includes('impact') ||
        optionScoreMap.size > 0;

      meta.set(normalizedKey, {
        isScoredField,
        optionScoreMap
      });
    });
    return meta;
  }, [exportCustomFields, normalizeCustomFieldKey]);

  const customFieldExportColumns = useMemo(() => {
    const columnMap = new Map();

    // Prefer configured custom field definitions so fields are exportable
    // even when no current row has a value.
    (Array.isArray(exportCustomFields) ? exportCustomFields : []).forEach((field) => {
      if (!field) return;

      const normalizedKey = normalizeCustomFieldKey(field.name || field.label);
      if (!normalizedKey) return;

      const labelBase = String(field.label || formatExportLabel(normalizedKey)).trim();
      const label = labelBase;
      columnMap.set(normalizedKey, {
        key: `custom__${normalizedKey}`,
        label
      });
    });

    // Keep fallback behavior for any custom keys present in data but not in definitions.
    allItemsData.forEach((item) => {
      const customFields = item?.custom_fields;
      if (!customFields || typeof customFields !== 'object' || Array.isArray(customFields)) return;
      Object.keys(customFields).forEach((key) => {
        const normalizedKey = normalizeCustomFieldKey(key);
        if (!normalizedKey || columnMap.has(normalizedKey)) return;
        columnMap.set(normalizedKey, {
          key: `custom__${normalizedKey}`,
          label: formatExportLabel(normalizedKey)
        });
      });
    });

    return Array.from(columnMap.entries())
      .sort((a, b) => a[1].label.localeCompare(b[1].label))
      .map(([, column]) => column);
  }, [allItemsData, exportCustomFields, formatExportLabel, normalizeCustomFieldKey]);

  // Create a set of item IDs that have pending approvals for quick lookup
  const itemsWithPendingApprovals = useMemo(() => {
    const itemIds = new Set();
    pendingApprovals.forEach(approval => {
      itemIds.add(approval.item_id);
    });
    return itemIds;
  }, [pendingApprovals]);

  useEffect(() => {
    setSelectedRows(prev => {
      if (!(prev instanceof Set) || prev.size === 0) {
        return prev instanceof Set ? prev : new Set();
      }

      const validIds = new Set(allItemsData.map(item => item.id));
      const filteredIds = Array.from(prev).filter(id => validIds.has(id));

      if (filteredIds.length === prev.size) {
        return prev;
      }

      return new Set(filteredIds);
    });
  }, [allItemsData]);

  useEffect(() => {
    setExpandedChildItems(prev => {
      if (!(prev instanceof Set) || prev.size === 0) {
        return prev instanceof Set ? prev : new Set();
      }

      const filteredIds = Array.from(prev).filter(id => parentIdsWithChildren.has(id));
      if (filteredIds.length === prev.size) {
        return prev;
      }

      return new Set(filteredIds);
    });
  }, [parentIdsWithChildren]);

  const exportColumns = useMemo(() => ([
    // Exported padded so the spreadsheet reads the same as the screen. Every
    // value in this file is written as text anyway (see formatValueForExport),
    // so the fixed width also keeps the column sorting correctly in Excel.
    // `|| null` hands a missing id back to formatValueForExport so it still
    // renders the sheet's '-' placeholder rather than an empty cell.
    { key: 'id', label: 'ID', exportValue: (item) => formatItemId(item.id) || null },
    { key: 'current_stage_name', label: 'Stage' },
    { key: 'title', label: 'Title' },
    { key: 'description', label: 'Description' },
    { key: 'initial_notes', label: 'Initial Notes' },
    { key: 'current_assessment_notes', label: 'Current Assessment Notes' },
    { key: 'immediate_actions_taken', label: 'Immediate Actions Taken' },
    { key: 'justification', label: 'Justification' },
    { key: 'management_comments', label: 'Management Comments' },
    { key: 'root_cause_title', label: 'Root Cause Title' },
    { key: 'root_cause_analysis', label: 'Root Cause Analysis' },
    { key: 'risk_zone', label: 'Risk Zone' },
    { key: 'category_name', label: 'Category' },
    { key: 'subcategory_name', label: 'Subcategory' },
    { key: 'project_name', label: 'Task Order' },
    { key: 'owner', label: 'Owner' },
    { key: 'primary_owner', label: 'Primary Owner' },
    { key: 'secondary_owner', label: 'Secondary Owner' },
    { key: 'owner_user_id', label: 'Owner User ID' },
    { key: 'created_by', label: 'Created By' },
    { key: 'created_on', label: 'Created On' },
    { key: 'updated_at', label: 'Updated At' },
    { key: 'status', label: 'Status' },
    { key: 'approval_status', label: 'Approval Status' },
    { key: 'close_comment', label: 'Close Notes' },
    { key: 'exportable', label: 'Exportable' },
    { key: 'task_count', label: 'Task Count' },
    { key: 'tasks', label: 'Tasks' },
    { key: 'no_action_items_required', label: 'No Action Items Required' },
    { key: 'original_idea_title', label: 'Original Idea Title' },
    ...customFieldExportColumns
  ]), [customFieldExportColumns]);

  // One row per task, written to a second worksheet in Excel exports.
  const taskExportColumns = useMemo(() => ([
    // Task ids are their own sequence and are not padded; the item id is.
    { key: 'task_id', label: 'Task ID' },
    { key: 'item_id', label: 'Item ID', exportValue: (task) => formatItemId(task.item_id) || null },
    { key: 'item_title', label: 'Item Title' },
    { key: 'description', label: 'Description' },
    { key: 'status', label: 'Status' },
    { key: 'assigned_to', label: 'Assigned To' },
    { key: 'due_date', label: 'Due Date' },
    { key: 'created_on', label: 'Created On' },
    { key: 'completed_on', label: 'Completed On' }
  ]), []);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ show: true, message, type });
  }, []);

  const handleCloseToast = useCallback(() => {
    setToast({ show: false, message: '', type: 'success' });
  }, []);

  const handleChildToggle = useCallback((event, itemId) => {
    event.preventDefault();
    event.stopPropagation();

    setExpandedChildItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  // Convert expandedItems Set to safe format for React components
  const safeExpandedChildRows = useMemo(() => {
    try {
      return expandedChildItems instanceof Set ? expandedChildItems : new Set();
    } catch (error) {
      console.warn('Error with expandedChildItems:', error);
      return new Set();
    }
  }, [expandedChildItems]);

  const isChildSectionExpanded = useCallback((itemId) => {
    try {
      return safeExpandedChildRows && safeExpandedChildRows.has && safeExpandedChildRows.has(itemId);
    } catch (error) {
      console.warn('Error checking child expanded state:', error);
      return false;
    }
  }, [safeExpandedChildRows]);

  // Applies the filters stored on a view. Kept in one place because three call
  // sites restore a view: an explicit view change, the restore-on-mount effect,
  // and saving edits to the view that is currently open.
  const applyViewFilters = useCallback((filters) => {
    if (!filters) return;

    setStageFilter(normalizeMultiFilter(stageFilterFromView(filters)));
    setProjectFilter(normalizeMultiFilter(filters.project));
    setCategoryFilter(normalizeMultiFilter(filters.category));

    // The view's own owner filter. The manual dropdown is left cleared so the two
    // never stack; picking an owner by hand replaces this one.
    setViewOwnerFilter(viewOwnerFilterFrom(filters));
    setOwnerFilter([]);
  }, []);

  // Views don't carry the secondary filters, so switching views clears them the
  // same way it clears the inline quick filters.
  const clearExtraFilters = useCallback(() => {
    setOwnerFilter([]);
    setViewOwnerFilter(null);
    setTypeFilter([]);
    setPrimaryOwnerFilter([]);
    setSecondaryOwnerFilter([]);
    setRiskZoneFilter([]);
  }, []);

  // View management handlers - must be defined before useEffects that use it
  const handleViewChange = useCallback((view) => {
    hasRestoredView.current = false;
    isViewChangeInProgress.current = true; // Flag that this is an explicit view change

    if (!isMyItemsRoute) {
      itemsRuntimeViewSelection = String(view);
    }
    
    clearExtraFilters();

    if (view === 'default') {
      setCurrentView('default');
      setViewConfig(null);
      setVisibleColumns(['id', 'current_stage_order', 'title', 'description', 'category_name', 'project_id', 'owner', 'created_on']);
      setStageFilter([]);
      setProjectFilter([]);
      setCategoryFilter([]);
      setSortConfig({ key: 'created_on', direction: 'desc' });
    } else {
      const selectedView = Array.isArray(userViews) ? userViews.find(v => v && v.id === parseInt(view)) : null;
      if (selectedView) {
        setCurrentView(view);
        setViewConfig(selectedView);
        setVisibleColumns(selectedView.visible_columns || ['id', 'current_stage_order', 'title', 'description', 'category_name', 'project_id', 'owner', 'created_on']);
        // Apply saved filters
        applyViewFilters(selectedView.filters);
        // Apply custom filters
        if (selectedView.custom_filters) {
          let parsedCustomFilters = [];
          if (typeof selectedView.custom_filters === 'string') {
            try {
              parsedCustomFilters = JSON.parse(selectedView.custom_filters);
            } catch (e) {
              console.warn('Failed to parse custom_filters:', e);
              parsedCustomFilters = [];
            }
          } else if (Array.isArray(selectedView.custom_filters)) {
            parsedCustomFilters = selectedView.custom_filters;
          }
          setCustomFilters(parsedCustomFilters);
        }
        // Apply saved sort
        if (selectedView.sort_config) {
          setSortConfig(selectedView.sort_config);
        }
      }
    }
  }, [userViews, isMyItemsRoute, clearExtraFilters, applyViewFilters]);

  // Save table state to localStorage when relevant state changes
  useEffect(() => {
    const stateToSave = {
      searchTerm,
      sortConfig,
      stageFilter,
      projectFilter,
      categoryFilter,
      ownerFilter,
      typeFilter,
      primaryOwnerFilter,
      secondaryOwnerFilter,
      riskZoneFilter,
      customFilters,
      expandedChildItems: Array.from(expandedChildItems),
      visibleColumns
    };
    saveTableState(stateToSave);
  }, [
    searchTerm,
    sortConfig,
    stageFilter,
    projectFilter,
    categoryFilter,
    ownerFilter,
    typeFilter,
    primaryOwnerFilter,
    secondaryOwnerFilter,
    riskZoneFilter,
    customFilters,
    expandedChildItems,
    visibleColumns
  ]);

  // Runtime view selection management:
  // - Keep user's last selected view while navigating within the SPA.
  // - On full refresh, this resets and DB default view is applied.
  useEffect(() => {
    if (userViews.length === 0) return;
    
    // If we're on /my-items route, always show My Items view
    if (isMyItemsRoute) {
      const myItemsView = userViews.find(view => view.view_name === 'My Items');
      if (myItemsView) {
        handleViewChange(myItemsView.id.toString());
      }
      return;
    }

    // Prefer runtime in-memory selection during this app session.
    const runtimeViewId = itemsRuntimeViewSelection;
    const hasRuntimeCustomView = runtimeViewId && runtimeViewId !== 'default' &&
      userViews.some(view => String(view?.id) === String(runtimeViewId));
    const defaultUserView = userViews.find(view => view?.is_default === true);

    if (hasRuntimeCustomView) {
      // Preserve explicit runtime selection when navigating pages.
      handleViewChange(runtimeViewId);
    } else if (runtimeViewId === 'default') {
      handleViewChange('default');
    } else if (defaultUserView) {
      // On refresh/new app session, fall back to DB default view.
      handleViewChange(String(defaultUserView.id));
    } else {
      // If no saved view (or saved view is default), preserve any locally-saved filter state.
      const hasLocalStorageState =
        savedState.sortConfig ||
        savedState.stageFilter ||
        savedState.projectFilter ||
        savedState.categoryFilter ||
        savedState.customFilters ||
        savedState.visibleColumns ||
        savedState.searchTerm;

      if (!hasLocalStorageState) {
        handleViewChange('default');
      }
    }
  }, [userViews, isMyItemsRoute, handleViewChange, savedState]);

  // Apply view configuration when currentView changes
  useEffect(() => {
    if (hasRestoredView.current && !isViewChangeInProgress.current) {
      return; // Don't apply automatic config after manual restoration unless it's an explicit view change
    }

    if (currentView === 'default') {
      // Only reset to defaults if we don't have saved localStorage state or if this is an explicit view change
      const hasLocalStorageState = savedState.sortConfig || savedState.stageFilter || savedState.projectFilter || savedState.categoryFilter;

      if (!hasLocalStorageState || isViewChangeInProgress.current) {
        setVisibleColumns(['id', 'current_stage_order', 'title', 'description', 'category_name', 'project_id', 'owner', 'created_on']);
        setStageFilter([]);
        setProjectFilter([]);
        setCategoryFilter([]);
        setViewOwnerFilter(null);
        setCustomFilters([]);
        setSortConfig({ key: 'created_on', direction: 'desc' });
      }
    } else {
      const selectedView = Array.isArray(userViews) ? userViews.find(v => v && v.id === parseInt(currentView)) : null;
      if (selectedView && selectedView.view_name) {
        // Only apply view settings if we don't have saved localStorage state or if this is an explicit view change
        const hasLocalStorageState = savedState.sortConfig || savedState.stageFilter || savedState.projectFilter || savedState.categoryFilter;

        if (!hasLocalStorageState || isViewChangeInProgress.current) {
          setVisibleColumns(selectedView.visible_columns || ['id', 'current_stage_order', 'title', 'description', 'category_name', 'project_id', 'owner', 'created_on']);
          // Apply saved filters
          applyViewFilters(selectedView.filters);
          // Apply saved sort
          if (selectedView.sort_config) {
            setSortConfig(selectedView.sort_config);
          }
        } else {
          // We have localStorage state, so only apply columns from the view but keep user's filters/sort
          setVisibleColumns(selectedView.visible_columns || ['id', 'current_stage_order', 'title', 'description', 'category_name', 'project_id', 'owner', 'created_on']);
        }
        
        // Always apply custom filters from the view, regardless of localStorage state
        if (selectedView.custom_filters) {
          let parsedCustomFilters = [];
          if (typeof selectedView.custom_filters === 'string') {
            try {
              parsedCustomFilters = JSON.parse(selectedView.custom_filters);
            } catch (e) {
              console.warn('Failed to parse custom_filters:', e);
              parsedCustomFilters = [];
            }
          } else if (Array.isArray(selectedView.custom_filters)) {
            parsedCustomFilters = selectedView.custom_filters;
          }
          setCustomFilters(parsedCustomFilters);
        }
        hasRestoredView.current = true;
      }
    }
    
    // Reset the flag after processing
    isViewChangeInProgress.current = false;
  }, [currentView, userViews, savedState, applyViewFilters]);

  // Handle row click to navigate directly to item detail page
  const handleRowClick = (event, item) => {
    event.preventDefault();
    event.stopPropagation();
    const targetUrl = `/items/${item.id}`;

    if (event?.button === 1 || event?.metaKey || event?.ctrlKey) {
      window.open(targetUrl, '_blank', 'noopener');
      return;
    }

    navigate(targetUrl);
  };

  const handleConfirmDelete = async () => {
    if (deleteModal.item) {
      try {
        await deleteItemMutation.mutateAsync(deleteModal.item.id);
        showToast(`Item ${formatItemRef(deleteModal.item.id)} moved to trash`, 'success');
        setDeleteModal({ show: false, item: null });
      } catch (error) {
        showToast(`Failed to delete item ${formatItemRef(deleteModal.item.id)}`, 'error');
        console.error('Delete error:', error);
      }
    }
  };

  // Opens an unsaved draft. Nothing is written until the user saves it there,
  // so abandoning the page can no longer leave an empty item behind.
  const handleCreateNewItem = () => {
    navigate('/items/new');
  };

  const handleSaveView = async (viewData, editingView) => {
    console.log('ItemsListView handleSaveView called with:');
    console.log('viewData:', viewData);
    console.log('editingView:', editingView);
    
    const viewPayload = {
      ...viewData,
      table_name: 'items'
    };

    // Carry the current column widths onto the view being saved, but only when
    // those widths belong to it: creating a view, or editing the active one.
    // Global views never store widths - they are shared, so widths stay per-user
    // in localStorage (see useColumnWidths).
    const widthsBelongToTargetView = !editingView || parseInt(currentView, 10) === editingView.id;
    if (widthsBelongToTargetView && !viewPayload.is_global && !editingView?.is_global) {
      viewPayload.column_widths = columnWidths;
    }

    console.log('ItemsListView viewPayload:', viewPayload);

    try {
      if (editingView) {
        console.log('Updating items view with ID:', editingView.id);
        const updateData = {
          id: editingView.id,
          ...viewPayload
        };
        console.log('ItemsListView updateData:', updateData);
        
        const result = await updateViewMutation.mutateAsync(updateData);
        console.log('ItemsListView update result:', result);
        
        // Update current view state to reflect the saved changes
        if (parseInt(currentView) === editingView.id) {
          setVisibleColumns(viewData.visible_columns);
          applyViewFilters(viewData.filters);
          if (viewData.custom_filters) {
            let parsedCustomFilters = [];
            if (typeof viewData.custom_filters === 'string') {
              try {
                parsedCustomFilters = JSON.parse(viewData.custom_filters);
              } catch (e) {
                console.warn('Failed to parse custom_filters:', e);
                parsedCustomFilters = [];
              }
            } else if (Array.isArray(viewData.custom_filters)) {
              parsedCustomFilters = viewData.custom_filters;
            }
            setCustomFilters(parsedCustomFilters);
          }
          if (viewData.sort_config) {
            setSortConfig(viewData.sort_config);
          }
        }
        
        showToast('View updated successfully');
      } else {
        const result = await createViewMutation.mutateAsync(viewPayload);
        if (!isMyItemsRoute) {
          itemsRuntimeViewSelection = String(result.id);
        }
        setCurrentView(result.id.toString());
        showToast('View created successfully');
      }
    } catch (error) {
      console.error('Error saving view:', error);
      showToast('Failed to save view', 'error');
    }
  };

  // The default view is a per-user preference stored separately from the view, so a
  // user can default to a global view they don't own. Clearing it (null) falls back
  // to the built-in "All Items" view.
  const handleSetDefaultView = async (view) => {
    try {
      if (view === 'default') {
        await setDefaultViewMutation.mutateAsync({ tableName: 'items', viewId: null });
        showToast('"All Items" is now your default view');
      } else if (view?.id) {
        await setDefaultViewMutation.mutateAsync({ tableName: 'items', viewId: view.id });
        showToast(`"${view.view_name}" is now your default view`);
      }
    } catch (error) {
      console.error('Error setting default view:', error);
      showToast('Failed to set default view', 'error');
    }
  };

  const handleDeleteView = (view) => {
    setDeleteViewModal({ show: true, view });
  };

  const handleConfirmDeleteView = async () => {
    if (deleteViewModal.view) {
      try {
        await deleteViewMutation.mutateAsync(deleteViewModal.view.id);
        showToast('View deleted successfully');
        if (parseInt(currentView) === deleteViewModal.view.id) {
          handleViewChange('default');
        }
        setDeleteViewModal({ show: false, view: null });
      } catch (error) {
        showToast('Failed to delete view', 'error');
        console.error('Delete view error:', error);
      }
    }
  };

  // Filter dropdown options must come from the full dataset. If they are built
  // from already-filtered rows, selecting one option can hide the others.
  const allItemsForFilterOptions = useMemo(
    () => [...openItems, ...closedItems],
    [openItems, closedItems]
  );

  // Get unique values for filters
  // Stages come from the workflow definition so they list in workflow order
  // (Create, Endorse, Plan, ...) rather than alphabetically. Any stage present on
  // an item but missing from the definition is appended so nothing is unfilterable.
  const stageOptions = useMemo(() => {
    const seen = new Set();
    const options = [];

    workflowStages.forEach(stage => {
      const name = stage?.stage_name;
      if (!name || seen.has(name)) return;
      seen.add(name);
      options.push({ value: name, label: name });
    });

    [...new Set(allItemsForFilterOptions.map(item => item.current_stage_name).filter(Boolean))]
      .sort()
      .forEach(name => {
        if (seen.has(name)) return;
        seen.add(name);
        options.push({ value: name, label: name });
      });

    return options;
  }, [workflowStages, allItemsForFilterOptions]);

  const projectOptions = useMemo(() => {
    const projects = [...new Set(allItemsForFilterOptions.map(item => item.project_name).filter(Boolean))].sort();
    return projects.map(project => ({ value: project, label: project }));
  }, [allItemsForFilterOptions]);

  const categoryOptions = useMemo(() => {
    const categories = [...new Set(allItemsForFilterOptions.map(item => item.category_name).filter(Boolean))].sort();
    return categories.map(category => ({ value: category, label: category }));
  }, [allItemsForFilterOptions]);

  // Owner fields store emails; label them with the directory name so the options
  // read the same way the table does.
  const buildUserFilterOptions = useCallback((fieldKey) => {
    const values = [...new Set(allItemsForFilterOptions.map(item => item[fieldKey]).filter(Boolean))];
    return values
      .map(value => ({ value, label: getOwnerDisplay({ owner: value }) || value }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allItemsForFilterOptions, getOwnerDisplay]);

  const ownerOptions = useMemo(() => buildUserFilterOptions('owner'), [buildUserFilterOptions]);
  const primaryOwnerOptions = useMemo(() => buildUserFilterOptions('primary_owner'), [buildUserFilterOptions]);
  const secondaryOwnerOptions = useMemo(() => buildUserFilterOptions('secondary_owner'), [buildUserFilterOptions]);

  // Types come from the configured choice list, with anything already on an item
  // appended so legacy values stay filterable.
  const typeOptions = useMemo(() => {
    const seen = new Set();
    const options = [];

    (allChoices?.typeChoices || []).forEach(choice => {
      const value = choice?.type;
      if (!value || seen.has(value)) return;
      seen.add(value);
      options.push({ value, label: value });
    });

    [...new Set(allItemsForFilterOptions.map(item => item.type).filter(Boolean))]
      .sort()
      .forEach(value => {
        if (seen.has(value)) return;
        seen.add(value);
        options.push({ value, label: value });
      });

    return options;
  }, [allChoices, allItemsForFilterOptions]);

  // Buckets, not raw scores: "High (13 - 19)".
  const riskZoneOptions = useMemo(() => (
    riskZoneBuckets.map(zone => ({
      value: zone.name,
      label: `${zone.name} (${zone.min} - ${zone.max})`
    }))
  ), [riskZoneBuckets]);

  // Group tasks by parent for expanded items
  const tasksByParent = useMemo(() => {
    const grouped = {};
    if (allTasks && Array.isArray(allTasks)) {
      allTasks.forEach(task => {
        if (task.parent_id) {
          if (!grouped[task.parent_id]) {
            grouped[task.parent_id] = [];
          }
          grouped[task.parent_id].push(task);
        }
      });
    }
    return grouped;
  }, [allTasks]);

  // Sort tasks the way a reader expects them: soonest due first, undated last.
  const sortTasksForExport = useCallback((tasks) => (
    [...tasks].sort((a, b) => {
      const dateA = a?.due_date ? new Date(a.due_date).getTime() : Number.POSITIVE_INFINITY;
      const dateB = b?.due_date ? new Date(b.due_date).getTime() : Number.POSITIVE_INFINITY;
      if (dateA !== dateB) return dateA - dateB;
      return (a?.id || 0) - (b?.id || 0);
    })
  ), []);

  const getTaskAssigneeDisplay = useCallback((task) => {
    if (task?.assigned_to_user_name) return task.assigned_to_user_name;

    // getOwnerDisplay resolves ids/emails/names against the user directory.
    return getOwnerDisplay({
      owner: task?.assigned_to,
      owner_user_id: task?.assigned_to_user_id ?? null
    });
  }, [getOwnerDisplay]);

  // Human-readable one-line-per-task summary for the "Tasks" column. This is the
  // only place tasks appear in a CSV export, so it has to stand on its own.
  const buildTasksSummary = useCallback((itemId) => {
    const tasks = tasksByParent[itemId] || [];
    if (tasks.length === 0) return '-';

    return sortTasksForExport(tasks)
      .map(task => {
        const parts = [`#${task.id}`];
        if (task.status) parts.push(task.status);
        if (task.due_date) parts.push(`Due ${formatDate(task.due_date)}`);

        const assignee = getTaskAssigneeDisplay(task);
        if (assignee && assignee !== '-') parts.push(assignee);

        const description = String(task.description || 'No description').replace(/\s+/g, ' ').trim();
        return `${parts.join(' | ')} - ${description}`;
      })
      .join('\n');
  }, [tasksByParent, sortTasksForExport, getTaskAssigneeDisplay]);

  // Flat task rows for the Excel "Tasks" worksheet, limited to the items being exported.
  const buildTaskExportRows = useCallback((itemIds) => {
    const ids = itemIds instanceof Set ? itemIds : new Set(itemIds);
    const titleById = new Map(allItemsData.map(item => [item.id, item.title]));

    const rows = [];
    ids.forEach(itemId => {
      const tasks = tasksByParent[itemId] || [];
      sortTasksForExport(tasks).forEach(task => {
        rows.push({
          task_id: task.id,
          item_id: itemId,
          item_title: titleById.get(itemId) || '-',
          description: task.description || '-',
          status: task.status || '-',
          assigned_to: getTaskAssigneeDisplay(task),
          due_date: task.due_date || null,
          created_on: task.created_on || null,
          completed_on: task.completed_on || null
        });
      });
    });

    // Keep the sheet in item order, then task order within each item.
    return rows.sort((a, b) => (a.item_id - b.item_id) || (a.task_id - b.task_id));
  }, [allItemsData, tasksByParent, sortTasksForExport, getTaskAssigneeDisplay]);

  // Task hover handlers
  const handleTaskHoverEnter = useCallback((event, itemId) => {
    if (isMobile) return; // Disable hover popup on mobile
    event.preventDefault();
    event.stopPropagation();
    
    // Clear any existing timeouts
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      setHideTimeout(null);
    }

    // If popup is already open for this item and not closing, keep it open
    if (taskHoverPopup.show && taskHoverPopup.itemId === itemId && !taskHoverPopup.closing) {
      return;
    }
    
    // Clear any existing timeouts
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      setHideTimeout(null);
    }
    
    // Hide any existing popup immediately when starting a new hover
    setTaskHoverPopup(prev => ({ ...prev, show: false, closing: false }));
    
    // Capture the current target reference before the timeout
    const currentTarget = event.currentTarget;
    
    // Set new timeout for 100ms delay
    const timeout = setTimeout(() => {
      // Check if the element still exists before getting bounding rect
      if (!currentTarget || !currentTarget.getBoundingClientRect) {
        return;
      }
      
      const rect = currentTarget.getBoundingClientRect();
      const itemTasks = tasksByParent[itemId] || [];
      
      // Get child items of this parent
      const childItems = hierarchicalOpenItems.find(item => item.id === itemId)?.children || [];
      
      // Collect tasks from child items
      const childTasks = [];
      childItems.forEach(childItem => {
        const childItemTasks = tasksByParent[childItem.id] || [];
        childTasks.push(...childItemTasks);
      });
      
      setTaskHoverPopup({
        show: true,
        itemId: itemId,
        position: {
          x: rect.right + 5, // Reduce gap - position closer to the hover target
          y: rect.top,
          anchorBottom: rect.bottom
        },
        tasks: itemTasks,
        childTasks: childTasks,
        expanded: false,
        closing: false
      });
    }, 100);
    
    setHoverTimeout(timeout);
  }, [hoverTimeout, hideTimeout, tasksByParent, hierarchicalOpenItems, taskHoverPopup.show, taskHoverPopup.itemId, taskHoverPopup.closing, isMobile]);

  const handleTaskHoverLeave = useCallback((event) => {
    // Clear the timeout if mouse leaves before delay completes
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    
    // Check if mouse is moving to the popup itself
    const relatedTarget = event.relatedTarget;
    if (relatedTarget && relatedTarget.closest && relatedTarget.closest('.task-hover-popup')) {
      return; // Don't hide if moving to popup
    }
    
    // Set hide timeout with a small delay to allow movement to popup
    const timeout = setTimeout(() => {
      setTaskHoverPopup(prev => ({ ...prev, closing: true }));
      // Hide after animation completes
      setTimeout(() => {
        setTaskHoverPopup(prev => ({ ...prev, show: false, closing: false }));
      }, 150);
    }, 100);
    
    setHideTimeout(timeout);
  }, [hoverTimeout]);

  const handleTaskPopupMouseEnter = useCallback(() => {
    // Clear any pending hide timeout when mouse enters popup
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      setHideTimeout(null);
    }
    // Clear show timeout if still pending
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  }, [hoverTimeout, hideTimeout]);

  const handleTaskPopupMouseLeave = useCallback(() => {
    setTaskHoverPopup(prev => ({ ...prev, closing: true }));
    // Hide after animation completes
    setTimeout(() => {
      setTaskHoverPopup(prev => ({ ...prev, show: false, closing: false }));
    }, 150);
  }, []);

  const handleExpandToggle = useCallback(() => {
    setTaskHoverPopup(prev => ({ ...prev, expanded: !prev.expanded }));
  }, []);

  // Keep the popup inside the viewport: drop down from the row by default, but flip
  // above it when there isn't room below (rows near the bottom of the window).
  useLayoutEffect(() => {
    if (!taskHoverPopup.show) return;

    const popup = taskPopupRef.current;
    if (!popup) return;

    const VIEWPORT_MARGIN = 8;
    const height = popup.offsetHeight;
    const anchorTop = taskHoverPopup.position.y;
    const anchorBottom = taskHoverPopup.position.anchorBottom || anchorTop;

    let top = anchorTop;
    let flipped = false;

    if (anchorTop + height > window.innerHeight - VIEWPORT_MARGIN) {
      // Align the popup's bottom edge with the row's bottom edge
      top = anchorBottom - height;
      flipped = true;

      if (top < VIEWPORT_MARGIN) {
        // Taller than the space above too - clamp within the viewport
        top = Math.max(VIEWPORT_MARGIN, window.innerHeight - VIEWPORT_MARGIN - height);
        flipped = top < anchorTop;
      }
    }

    setTaskPopupPlacement(prev => (
      prev.top === top && prev.flipped === flipped ? prev : { top, flipped }
    ));
  }, [
    taskHoverPopup.show,
    taskHoverPopup.itemId,
    taskHoverPopup.expanded,
    taskHoverPopup.position.y,
    taskHoverPopup.position.anchorBottom,
    taskHoverPopup.tasks,
    taskHoverPopup.childTasks
  ]);

  const handleTaskClick = useCallback((event, taskId) => {
    event.preventDefault();
    event.stopPropagation();
    setTaskHoverPopup(prev => ({ ...prev, closing: true }));
    // Hide after animation completes
    setTimeout(() => {
      setTaskHoverPopup(prev => ({ ...prev, show: false, closing: false }));
    }, 150);
    navigate(`/tasks/${taskId}`);
  }, [navigate]);

  const getCustomFieldDisplayValue = useCallback((item, normalizedKey) => {
    const customFields = item?.custom_fields;
    if (!customFields || typeof customFields !== 'object' || Array.isArray(customFields)) {
      return '-';
    }

    for (const rawKey in customFields) {
      if (!Object.prototype.hasOwnProperty.call(customFields, rawKey)) {
        continue;
      }

      if (normalizeCustomFieldKey(rawKey) === normalizedKey) {
        return customFields[rawKey] ?? '-';
      }
    }

    return '-';
  }, [normalizeCustomFieldKey]);

  // Define columns with full task expansion functionality and hierarchy support
  const columns = useMemo(() => {
    const baseColumns = [
    {
      key: 'id',
      label: 'ID',
      className: 'id-cell',
      render: (item, context = {}) => {
        const {
          isChild = false,
          parentId = null,
          indentLevel = 0
        } = context;
        const indentation = indentLevel > 0 ? indentLevel * 16 : 0;

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              paddingLeft: `${indentation}px`,
              width: '100%'
            }}
          >
            {/* Child indicator */}
            {isChild && (
              <div
                className="child-indicator"
                style={{
                  width: '12px',
                  height: '1px',
                  backgroundColor: '#dee2e6',
                  position: 'relative'
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    left: '0',
                    top: '-6px',
                    width: '1px',
                    height: '12px',
                    backgroundColor: '#dee2e6'
                  }}
                ></div>
              </div>
            )}

            <span>{formatItemRef(item.id)}</span>

            {/* Child badge */}
            {isChild && parentId && (
              <span
                className="child-badge"
                style={{
                  backgroundColor: '#e3f2fd',
                  color: '#1565c0',
                  padding: '2px 6px',
                  borderRadius: '10px',
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  border: '1px solid #bbdefb'
                }}
                title={`Child of item ${formatItemRef(parentId)}`}
              >
                Child of {formatItemRef(parentId)}
              </span>
            )}

            {/* A closed child still lists under its open parent, so say so plainly */}
            {isChild && item.status === 'closed' && (
              <span
                className="child-closed-badge"
                style={{
                  backgroundColor: '#f1f3f5',
                  color: '#6c757d',
                  padding: '2px 6px',
                  borderRadius: '10px',
                  fontSize: '0.7rem',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  border: '1px solid #ced4da'
                }}
                title="This child item is closed"
              >
                Closed
              </span>
            )}

            {/* Task hover target - only show on parent items or items without parent */}
            {(item.total_task_count > 0 || item.task_count > 0) && !item.child_of && (
              <div
                className="task-hover-target"
                onMouseEnter={(e) => handleTaskHoverEnter(e, item.id)}
                onMouseLeave={handleTaskHoverLeave}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  backgroundColor: '#f9fafb',
                  minWidth: '32px',
                  minHeight: '26px',
                  position: 'relative'
                }}
                title={`View ${item.total_task_count || item.task_count} task(s)`}
              >
                <i className="bi bi-list-task"></i>
                <span>{item.total_task_count || item.task_count}</span>
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'current_stage_order',
      label: 'Stage',
      className: 'stage-cell',
      render: (item) => (
        <div className="stage-container">
          <span 
            className={`stage-pill ${itemsWithPendingApprovals.has(item.id) ? 'has-approval' : ''}`}
            data-tooltip={itemsWithPendingApprovals.has(item.id) ? 'Awaiting Approval' : undefined}
          >
            {item.current_stage_name || 'No Stage'}
            {itemsWithPendingApprovals.has(item.id) && (
              <i 
                className="approval-icon bi bi-clock" 
              ></i>
            )}
          </span>
        </div>
      )
    },
    {
      key: 'title',
      label: 'Title',
      className: 'title-cell',
      render: (item) => item.title
    },
    {
      key: 'description',
      label: 'Description',
      className: 'description-cell',
      render: (item) => (
        item.description 
          ? item.description.length > 100 
            ? `${item.description.substring(0, 100)}...`
            : item.description
          : '-'
      )
    },
    {
      key: 'category_name',
      label: 'Category'
    },
    {
      key: 'project_id',
      label: 'Task Order',
      render: (item) => item.project_name || '-'
    },
    {
      key: 'owner',
      label: 'Owner',
      render: (item) => getOwnerDisplay(item)
    },
    {
      key: 'created_on',
      label: 'Created On',
      className: 'date-cell',
      render: (item) => formatDate(item.created_on)
    },
    {
      key: 'exportable',
      label: 'Exportable',
      className: 'exportable-cell',
      render: (item) => item.exportable || 'No'
    },
    {
      key: 'status',
      label: 'Status',
      render: (item) => item.status || '-'
    },
    {
      key: 'approval_status',
      label: 'Approval Status',
      render: (item) => item.approval_status || '-'
    },
    {
      key: 'close_comment',
      label: 'Close Notes',
      render: (item) => item.close_comment || '-'
    },
    {
      key: 'created_by',
      label: 'Created By',
      render: (item) => item.created_by || '-'
    },
    {
      key: 'updated_at',
      label: 'Updated At',
      className: 'date-cell',
      render: (item) => formatDate(item.updated_at)
    },
    {
      key: 'subcategory_name',
      label: 'Subcategory',
      render: (item) => item.subcategory_name || '-'
    },
    {
      key: 'risk_zone',
      label: 'Risk Zone',
      render: (item) => item.risk_zone || '-'
    },
    {
      key: 'initial_notes',
      label: 'Initial Notes',
      render: (item) => item.initial_notes || '-'
    },
    {
      key: 'current_assessment_notes',
      label: 'Current Assessment Notes',
      render: (item) => item.current_assessment_notes || '-'
    },
    {
      key: 'immediate_actions_taken',
      label: 'Immediate Actions Taken',
      render: (item) => item.immediate_actions_taken || '-'
    },
    {
      key: 'justification',
      label: 'Justification',
      render: (item) => item.justification || '-'
    },
    {
      key: 'management_comments',
      label: 'Management Comments',
      render: (item) => item.management_comments || '-'
    },
    {
      key: 'root_cause_title',
      label: 'Root Cause Title',
      render: (item) => item.root_cause_title || '-'
    },
    {
      key: 'root_cause_analysis',
      label: 'Root Cause Analysis',
      render: (item) => item.root_cause_analysis || '-'
    },
    {
      key: 'primary_owner',
      label: 'Primary Owner',
      render: (item) => item.primary_owner || '-'
    },
    {
      key: 'secondary_owner',
      label: 'Secondary Owner',
      render: (item) => item.secondary_owner || '-'
    },
    {
      key: 'owner_user_id',
      label: 'Owner User ID',
      render: (item) => item.owner_user_id || '-'
    },
    {
      key: 'task_count',
      label: 'Task Count',
      render: (item) => item.task_count || 0
    },
    {
      key: 'no_action_items_required',
      label: 'No Action Items Required',
      render: (item) => {
        if (item.no_action_items_required === null || item.no_action_items_required === undefined) {
          return '-';
        }
        return item.no_action_items_required ? 'Yes' : 'No';
      }
    },
    {
      key: 'original_idea_title',
      label: 'Original Idea Title',
      render: (item) => item.original_idea_title || '-'
    },
    {
      key: 'assigned_discipline',
      label: 'Assigned Discipline',
      render: (item) => item.assigned_discipline || '-'
    },
    {
      key: 'originating_discipline',
      label: 'Originating Discipline',
      render: (item) => item.originating_discipline || '-'
    },
    {
      key: 'type',
      label: 'Type',
      render: (item) => item.type || '-'
    },
    {
      key: 'risk_type',
      label: 'Risk Type',
      render: (item) => item.risk_type || '-'
    },
    {
      key: 'opportunity_or_threat',
      label: 'Opportunity or Threat',
      render: (item) => item.opportunity_or_threat || '-'
    },
    {
      key: 'probability',
      label: 'Probability',
      render: (item) => (item.probability ?? '-')
    },
    {
      key: 'cost',
      label: 'Cost',
      render: (item) => (item.cost ?? '-')
    },
    {
      key: 'schedule',
      label: 'Schedule',
      render: (item) => (item.schedule ?? '-')
    },
    {
      key: 'detectability',
      label: 'Detectability',
      render: (item) => (item.detectability ?? '-')
    },
    {
      key: 'root_cause_category',
      label: 'Root Cause Category',
      render: (item) => item.root_cause_category || '-'
    },
    {
      key: 'is_root_cause_analysis_required',
      label: 'Root Cause Analysis Required',
      render: (item) => {
        if (item.is_root_cause_analysis_required === null || item.is_root_cause_analysis_required === undefined) {
          return '-';
        }
        return item.is_root_cause_analysis_required ? 'Yes' : 'No';
      }
    },
    {
      key: 'endorser',
      label: 'Endorser',
      render: (item) => item.endorser || '-'
    },
    {
      key: 'endorsement_notes',
      label: 'Endorsement Notes',
      render: (item) => item.endorsement_notes || '-'
    },
    {
      key: 'child_of',
      label: 'Parent Item',
      render: (item) => (item.child_of ? formatItemRef(item.child_of) : '-')
    }
    ];

    const dynamicCustomColumns = customFieldExportColumns.map((column) => {
      const normalizedKey = String(column.key || '').replace(/^custom__/, '');
      return {
        key: column.key,
        label: column.label,
        render: (item) => getCustomFieldDisplayValue(item, normalizedKey)
      };
    });

    return [...baseColumns, ...dynamicCustomColumns];
  }, [
    itemsWithPendingApprovals,
    handleTaskHoverEnter,
    handleTaskHoverLeave,
    getOwnerDisplay,
    customFieldExportColumns,
    getCustomFieldDisplayValue
  ]);

  // Filter configuration for SearchFilters component
  const filters = [
    {
      icon: () => <i className="bi bi-signpost-split-fill"></i>,
      value: stageOptions.filter(option => stageFilter.includes(option.value)),
      onChange: (selectedOptions) => setStageFilter(Array.isArray(selectedOptions) ? selectedOptions.map(opt => opt.value) : []),
      options: stageOptions,
      placeholder: 'All stages',
      isClearable: true,
      isMulti: true
    },
    {
      icon: () => <i className="bi bi-folder-fill"></i>,
      value: projectOptions.filter(option => projectFilter.includes(option.value)),
      onChange: (selectedOptions) => setProjectFilter(Array.isArray(selectedOptions) ? selectedOptions.map(opt => opt.value) : []),
      options: projectOptions,
      placeholder: 'All task orders',
      isClearable: true,
      isMulti: true
    },
    {
      icon: () => <i className="bi bi-tag-fill"></i>,
      value: categoryOptions.filter(option => categoryFilter.includes(option.value)),
      onChange: (selectedOptions) => setCategoryFilter(Array.isArray(selectedOptions) ? selectedOptions.map(opt => opt.value) : []),
      options: categoryOptions,
      placeholder: 'All categories',
      isClearable: true,
      isMulti: true
    },
    {
      icon: () => <i className="bi bi-archive-fill"></i>,
      value: [
        { value: 'open', label: 'Open Items' },
        { value: 'closed', label: 'Closed Items' }
      ].filter(option => itemStatusFilter.includes(option.value)),
      onChange: (selectedOptions) => {
        const selectedValues = Array.isArray(selectedOptions)
          ? selectedOptions.map(option => option.value).filter(value => value === 'open' || value === 'closed')
          : [];
        setItemStatusFilter(selectedValues);
        setSelectedRows(new Set());
      },
      options: [
        { value: 'open', label: 'Open Items' },
        { value: 'closed', label: 'Closed Items' }
      ],
      placeholder: 'Item status',
      isClearable: true,
      isMulti: true
    }
  ];

  // Secondary filters - same shape as the inline ones, shown in the popover
  const extraFilters = [
    {
      key: 'owner',
      label: 'Owner',
      icon: () => <i className="bi bi-person-fill"></i>,
      value: ownerOptions.filter(option => ownerFilter.includes(option.value)),
      onChange: (selectedOptions) => {
        // Choosing an owner by hand replaces the view's owner filter, so clearing
        // the dropdown really does clear it.
        setViewOwnerFilter(null);
        setOwnerFilter(Array.isArray(selectedOptions) ? selectedOptions.map(opt => opt.value) : []);
      },
      options: ownerOptions,
      placeholder: 'All owners',
      isClearable: true,
      isMulti: true
    },
    {
      key: 'type',
      label: 'Type',
      icon: () => <i className="bi bi-bookmark-fill"></i>,
      value: typeOptions.filter(option => typeFilter.includes(option.value)),
      onChange: (selectedOptions) => setTypeFilter(Array.isArray(selectedOptions) ? selectedOptions.map(opt => opt.value) : []),
      options: typeOptions,
      placeholder: 'All types',
      isClearable: true,
      isMulti: true
    },
    {
      key: 'primary_owner',
      label: 'Primary Owner',
      icon: () => <i className="bi bi-person-check-fill"></i>,
      value: primaryOwnerOptions.filter(option => primaryOwnerFilter.includes(option.value)),
      onChange: (selectedOptions) => setPrimaryOwnerFilter(Array.isArray(selectedOptions) ? selectedOptions.map(opt => opt.value) : []),
      options: primaryOwnerOptions,
      placeholder: 'All primary owners',
      isClearable: true,
      isMulti: true
    },
    {
      key: 'secondary_owner',
      label: 'Secondary Owner',
      icon: () => <i className="bi bi-person-plus-fill"></i>,
      value: secondaryOwnerOptions.filter(option => secondaryOwnerFilter.includes(option.value)),
      onChange: (selectedOptions) => setSecondaryOwnerFilter(Array.isArray(selectedOptions) ? selectedOptions.map(opt => opt.value) : []),
      options: secondaryOwnerOptions,
      placeholder: 'All secondary owners',
      isClearable: true,
      isMulti: true
    },
    {
      key: 'risk_zone',
      label: 'Risk Zone',
      icon: () => <i className="bi bi-exclamation-diamond-fill"></i>,
      value: riskZoneOptions.filter(option => riskZoneFilter.includes(option.value)),
      onChange: (selectedOptions) => setRiskZoneFilter(Array.isArray(selectedOptions) ? selectedOptions.map(opt => opt.value) : []),
      options: riskZoneOptions,
      placeholder: 'All risk zones',
      isClearable: true,
      isMulti: true
    }
  ];

  // Export data preparation
  const exportData = useMemo(() => {
    const toScoredExportValue = (rawValue, optionScoreMap) => {
      if (rawValue === null || rawValue === undefined) return '-';

      const rawText = String(rawValue).trim();
      if (!rawText) return '-';

      if (/^-?\d+(\.\d+)?$/.test(rawText)) {
        return Number(rawText);
      }

      const normalized = normalizeCustomFieldKey(rawText);
      if (normalized && optionScoreMap && optionScoreMap.has(normalized)) {
        return optionScoreMap.get(normalized);
      }

      const prefixedScoreMatch = rawText.match(/^(-?\d+)\s*[-:]/);
      if (prefixedScoreMatch) {
        const parsed = parseInt(prefixedScoreMatch[1], 10);
        if (Number.isFinite(parsed)) return parsed;
      }

      // For scored fields, avoid exporting words when we cannot resolve to a score.
      return '-';
    };

    return allItemsData.map(item => {
      const row = {
        id: item.id,
        current_stage_name: item.current_stage_name || 'No Stage',
        title: item.title || '-',
        description: item.description || '-',
        initial_notes: item.initial_notes || '-',
        current_assessment_notes: item.current_assessment_notes || '-',
        immediate_actions_taken: item.immediate_actions_taken || '-',
        justification: item.justification || '-',
        management_comments: item.management_comments || '-',
        root_cause_title: item.root_cause_title || '-',
        root_cause_analysis: item.root_cause_analysis || '-',
        risk_zone: item.risk_zone || '-',
        category_name: item.category_name || '-',
        subcategory_name: item.subcategory_name || '-',
        project_name: item.project_name || '-',
        owner: getOwnerDisplay(item) || '-',
        primary_owner: item.primary_owner || '-',
        secondary_owner: item.secondary_owner || '-',
        owner_user_id: item.owner_user_id || '-',
        created_by: item.created_by || '-',
        created_on: item.created_on,
        updated_at: item.updated_at,
        status: item.status || '-',
        approval_status: item.approval_status || '-',
        close_comment: item.close_comment || '-',
        task_count: item.task_count || 0,
        tasks: buildTasksSummary(item.id),
        exportable: item.exportable || 'No',
        no_action_items_required: item.no_action_items_required,
        original_idea_title: item.original_idea_title || '-'
      };

      const customFields = item?.custom_fields;
      if (customFields && typeof customFields === 'object' && !Array.isArray(customFields)) {
        Object.entries(customFields).forEach(([key, value]) => {
          if (!key) return;

          const normalizedKey = normalizeCustomFieldKey(key);
          if (!normalizedKey) return;

          const fieldMeta = scoredCustomFieldMeta.get(normalizedKey);
          if (fieldMeta?.isScoredField) {
            row[`custom__${normalizedKey}`] = toScoredExportValue(value, fieldMeta.optionScoreMap);
          } else {
            row[`custom__${normalizedKey}`] = value ?? '-';
          }
        });
      }

      return row;
    });
  }, [allItemsData, getOwnerDisplay, normalizeCustomFieldKey, scoredCustomFieldMeta, buildTasksSummary]);

  const handleSelectionChange = useCallback((newSelection) => {
    setSelectedRows(newSelection instanceof Set ? newSelection : new Set());
  }, []);

  const handleBulkDelete = useCallback((selectionSet) => {
    if (!(selectionSet instanceof Set) || selectionSet.size === 0) {
      return;
    }

    setBulkDeleteModal({ show: true, selectedRows: selectionSet });
  }, []);

  const handleConfirmBulkDelete = useCallback(async () => {
    const { selectedRows: selectionSet } = bulkDeleteModal;
    if (!(selectionSet instanceof Set) || selectionSet.size === 0) {
      return;
    }

    setIsBulkDeleting(true);
    const ids = Array.from(selectionSet);

    try {
      await bulkDeleteItemsMutation.mutateAsync(ids);
      showToast(`${ids.length} item${ids.length !== 1 ? 's' : ''} moved to trash`, 'success');
      setSelectedRows(new Set());
      setBulkDeleteModal({ show: false, selectedRows: new Set() });
    } catch (error) {
      console.error('Bulk delete failed:', error);
      showToast('Failed to delete selected items', 'error');
    } finally {
      setIsBulkDeleting(false);
    }
  }, [bulkDeleteModal, bulkDeleteItemsMutation, showToast]);

  const handleExportSelected = useCallback(() => {
    if (selectedRows.size === 0) {
      showToast('No items selected for export', 'warning');
      return;
    }
    setShowExportModal(true);
  }, [selectedRows, showToast]);

  const handleClearSelection = useCallback(() => {
    setSelectedRows(new Set());
  }, []);

  const bulkActions = useMemo(() => ([
    {
      label: 'Export Selected',
      icon: 'bi bi-download',
      className: 'btn btn-success',
      disabled: isBulkExporting,
      onClick: handleExportSelected
    },
    {
      label: 'Move to Trash',
      icon: 'bi bi-trash3',
      className: 'btn btn-danger',
      disabled: isBulkDeleting,
      onClick: handleBulkDelete
    }
  ]), [handleBulkDelete, handleExportSelected, isBulkDeleting, isBulkExporting]);

  // Action buttons for header
  const actionButtons = [
    ...(!isMobile && canCreateItems ? [
      {
        label: 'New Item',
        icon: 'bi bi-plus-lg',
        onClick: handleCreateNewItem,
        className: 'btn btn-primary'
      }
    ] : []),
    {
      label: 'Export',
      icon: 'bi bi-upload',
      onClick: () => setShowExportModal(true),
      className: 'btn btn-outline-secondary'
    },
    // Import creates items, so it is gated the same way as New Item.
    ...(canCreateItems ? [
      {
        label: 'Import',
        icon: 'bi bi-download',
        onClick: () => navigate('/items/import'),
        className: 'btn btn-outline-secondary'
      }
    ] : [])
  ];

  const searchActions = isMobile && canCreateItems ? (
    <button
      type="button"
      className="btn btn-primary"
      onClick={handleCreateNewItem}
      aria-label="New Item"
      title="New Item"
    >
      <i className="bi bi-plus-circle"></i>
    </button>
  ) : null;

  const filteredExportData = useMemo(() => {
    if (selectedRows.size === 0) return exportData;
    return exportData.filter(item => selectedRows.has(item.id));
  }, [exportData, selectedRows]);

  const handleExportModalSave = useCallback(async ({ data, columns, filename, format }) => {
    setIsBulkExporting(true);
    try {
      if (format === 'pdf') {
        // For PDF export, we need to send the selected item IDs to the backend
        const itemIds = data.map(item => item.id);
        
        console.log('PDF Export: Starting export for', itemIds.length, 'items');
        
        try {
          let baseUrl = String(API_CONFIG?.BASE_URL || '').replace(/\/$/, '');
          // Match the apiUrl() normalization in useQueries: in dev, ensure base URL includes `/api`.
          if (baseUrl && process.env.NODE_ENV === 'development' && !/\/api$/i.test(baseUrl)) {
            baseUrl = `${baseUrl}/api`;
          }

          const exportUrl = baseUrl ? `${baseUrl}/items/export/pdf` : '/api/items/export/pdf';

          const response = await authenticatedFetch(exportUrl, {
            method: 'POST',
            body: JSON.stringify({ itemIds })
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
            throw new Error(errorData.error || 'Failed to generate PDF export');
          }

          const contentType = response.headers.get('content-type') || '';
          if (!contentType.toLowerCase().includes('application/pdf')) {
            const text = await response.text().catch(() => '');
            throw new Error(
              `Expected a PDF but received '${contentType || 'unknown'}'. ${text ? `Response: ${text.slice(0, 200)}` : ''}`
            );
          }

          // Get the PDF blob and trigger download
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          console.log('PDF Export: Successfully downloaded', filename);
        } catch (fetchError) {
          console.error('PDF Export: Authentication/Fetch error:', fetchError);
          throw fetchError;
        }
        
      } else if (format === 'xlsx') {
        // Tasks get their own worksheet alongside the items sheet. The Tasks
        // column on the items sheet carries the same data as text.
        const taskRows = buildTaskExportRows(data.map(item => item.id));
        await exportToExcel(data, columns, filename, {
          sheetName: 'Items',
          extraSheets: taskRows.length > 0
            ? [{ name: 'Tasks', columns: taskExportColumns, data: taskRows }]
            : []
        });
      } else {
        // CSV is single-sheet, so the Tasks text column is the whole story there.
        exportToCSV(data, columns, filename);
      }
      showToast(`Exported ${data.length} item${data.length !== 1 ? 's' : ''}`, 'success');
      setShowExportModal(false);
    } catch (error) {
      console.error('Export error:', error);
      showToast(error.message || 'Failed to export data', 'error');
    } finally {
      setIsBulkExporting(false);
    }
  }, [showToast, authenticatedFetch, buildTaskExportRows, taskExportColumns]);

  // Flatten hierarchical data for table display - only show parent items and hide children (they'll be rendered by DataTable)
  const flattenHierarchicalData = useCallback((hierarchicalItems) => {
    const flattened = [];

    const processItem = (item, parentId = null, indentLevel = 0, ancestorCollapsed = false) => {
      const children = Array.isArray(item.children) ? item.children : [];
      const hasChildren = children.length > 0;
      const isExpanded = hasChildren ? isChildSectionExpanded(item.id) : false;
      const isChild = parentId !== null;
      const isHiddenByParent = ancestorCollapsed;

      // Only add parent items to the flattened array - children will be rendered inline by DataTable
      if (!isChild) {
        flattened.push({
          ...item,
          _isChild: isChild,
          _parentId: parentId,
          _indentLevel: indentLevel,
          _hierarchyContext: {
            isChild,
            parentId,
            indentLevel,
            hasChildren,
            isExpanded,
            childCount: children.length,
            isHiddenByParent
          }
        });
      }
    };

    hierarchicalItems.forEach(item => processItem(item));
    return flattened;
  }, [isChildSectionExpanded]);

  // Create flattened data for active and completed sections
  const flattenedActiveData = useMemo(() => {
    const flattened = flattenHierarchicalData(hierarchicalOpenItems);
    // Attach children data to parent items for DataTable to render
    return flattened.map(item => {
      const hierarchicalItem = hierarchicalOpenItems.find(hi => hi.id === item.id);
      return {
        ...item,
        children: hierarchicalItem?.children || []
      };
    });
  }, [flattenHierarchicalData, hierarchicalOpenItems]);
  
  const flattenedCompletedData = useMemo(() => {
    const flattened = flattenHierarchicalData(hierarchicalClosedItems);
    // Attach children data to parent items for DataTable to render
    return flattened.map(item => {
      const hierarchicalItem = hierarchicalClosedItems.find(hi => hi.id === item.id);
      return {
        ...item,
        children: hierarchicalItem?.children || []
      };
    });
  }, [flattenHierarchicalData, hierarchicalClosedItems]);

  const normalizedStatusFilters = itemStatusFilter.length > 0 ? itemStatusFilter : ['open', 'closed'];
  const showOpenItems = normalizedStatusFilters.includes('open');
  const showClosedItems = normalizedStatusFilters.includes('closed');

  const displayedActiveData = [
    ...(showOpenItems ? flattenedActiveData : []),
    ...(showClosedItems ? flattenedCompletedData : [])
  ];
  const displayedCompletedData = [];

  const pageTitle = showClosedItems && !showOpenItems
    ? 'Closed Items'
    : (showOpenItems && !showClosedItems ? 'Open Items' : 'All Items');

  const itemLabel = showClosedItems && !showOpenItems
    ? 'closed items'
    : (showOpenItems && !showClosedItems ? 'open items' : 'items');

  const searchPlaceholder = showClosedItems && !showOpenItems
    ? 'Search closed items...'
    : (showOpenItems && !showClosedItems ? 'Search open items...' : 'Search all items...');

  return (
    <>
      <ListViewPage
        // Page configuration
        title={pageTitle}
        itemLabel={itemLabel}
        tableName="items"
        
        // Data and loading
        activeData={displayedActiveData}
        completedData={displayedCompletedData}
        loading={loading}
        error={error}
        
        // Columns
        columns={columns}
        defaultVisibleColumns={visibleColumns}
        visibleColumns={visibleColumns}
        onToggleColumn={(column) => {
          setVisibleColumns(prev => 
            prev.includes(column) 
              ? prev.filter(col => col !== column)
              : [...prev, column]
          );
        }}
        
        // Search and filtering
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={searchPlaceholder}
        filters={filters}
        extraFilters={extraFilters}
        searchActions={searchActions}
        
        // Sorting
        sortConfig={sortConfig}
        onSort={setSortConfig}
        
        // Actions and handlers
        onRowClick={handleRowClick}
        actionButtons={actionButtons}
        
        // Split view configuration
        completedSectionTitle="Closed Items"
        completedSectionIcon="bi-archive"
        
        // View management
        useViews={true}
        userViews={userViews}
        currentView={currentView}
        onViewChange={handleViewChange}
        onSaveView={handleSaveView}
        onDeleteView={handleDeleteView}
        onSetDefaultView={handleSetDefaultView}

        // Export functionality
        enableExport={false}
        exportData={exportData}
        exportFileName="items_export"
        
        // Custom filtering
        customFilter={customFilter}
        
        // Toast notifications
        toast={toast}
        onCloseToast={handleCloseToast}

        // Multi-select functionality
        enableMultiSelect={true}
        selectedRows={selectedRows}
        onSelectionChange={handleSelectionChange}
        selectableRowKey="id"
        bulkActions={bulkActions}
        
        // Custom row classes
        getRowClassName={(row) => {
          let className = '';
          if (row._isChild) {
            className += 'child-item-row ';
            if (row._hierarchyContext?.isHiddenByParent) {
              className += 'collapsed-child ';
            }
          }
          if (row._hierarchyContext?.hasChildren && !row._hierarchyContext?.isChild) {
            className += 'has-children-row ';
          }
          if (itemsWithPendingApprovals.has(row.id)) {
            className += 'has-pending-approval ';
          }
          return className.trim();
        }}

        // Child toggle control
        onChildToggle={handleChildToggle}

        // Virtualization (performance)
        virtualizeActiveTable={true}
        virtualizeCompletedTable={true}

        // Resizable columns (widths persist with the selected view)
        enableColumnResize={true}
        columnWidths={columnWidths}
        onColumnResize={handleColumnResize}
      />

      {/* Task Hover Popup */}
      {taskHoverPopup.show && (
        <div
          ref={taskPopupRef}
          className="task-hover-popup"
          style={{
            position: 'fixed',
            left: `${taskHoverPopup.position.x}px`,
            top: `${taskPopupPlacement.top ?? taskHoverPopup.position.y}px`,
            zIndex: 1000,
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            padding: '12px',
            minWidth: '280px',
            maxWidth: '400px',
            maxHeight: 'calc(100vh - 16px)',
            animation: taskHoverPopup.closing ? 'fadeOutScale 0.15s ease-in forwards' : 'fadeInScale 0.15s ease-out forwards',
            transformOrigin: taskPopupPlacement.flipped ? 'bottom left' : 'top left'
          }}
          onMouseEnter={handleTaskPopupMouseEnter}
          onMouseLeave={handleTaskPopupMouseLeave}
        >
          <div className="popup-header" style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px',
            borderBottom: '1px solid #f3f4f6',
            paddingBottom: '8px'
          }}>
            Tasks ({taskHoverPopup.tasks.length + (taskHoverPopup.childTasks?.length || 0)})
          </div>
          <div className="popup-tasks" style={{
            maxHeight: taskHoverPopup.expanded ? '300px' : '200px',
            overflowY: 'auto'
          }}>
            {(() => {
              const allTasks = [
                ...(taskHoverPopup.tasks || []),
                ...(taskHoverPopup.childTasks || []).map(task => ({ ...task, isChildTask: true }))
              ];
              
              const totalTasks = allTasks.length;
              const displayTasks = taskHoverPopup.expanded ? allTasks : allTasks.slice(0, 2);
              
              return (
                <>
                  {displayTasks.map(task => {
                    return (
                    <div
                      key={task.isChildTask ? `child-${task.id}` : task.id}
                      className="popup-task-item"
                      style={{
                        padding: '8px',
                        borderRadius: '4px',
                        marginBottom: '4px',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s ease',
                        border: '1px solid transparent',
                        backgroundColor: task.isChildTask ? '#f8f9fa' : 'transparent',
                        borderLeft: task.isChildTask ? '3px solid #dee2e6' : 'none'
                      }}
                      onClick={(e) => handleTaskClick(e, task.id)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = task.isChildTask ? '#e9ecef' : '#f9fafb';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = task.isChildTask ? '#f8f9fa' : 'transparent';
                        e.currentTarget.style.borderColor = 'transparent';
                      }}
                    >
                      {task.isChildTask && !taskHoverPopup.expanded && displayTasks.findIndex(t => t.isChildTask) === displayTasks.indexOf(task) && (
                        <div style={{
                          fontSize: '11px',
                          fontWeight: '600',
                          color: '#6b7280',
                          marginBottom: '4px',
                          textAlign: 'center',
                          borderBottom: '1px solid #e5e7eb',
                          paddingBottom: '4px'
                        }}>
                          Child Tasks
                        </div>
                      )}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '4px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{
                            fontSize: '12px',
                            fontWeight: '500',
                            color: '#6b7280'
                          }}>#{task.id}</span>
                          <span style={{
                            fontSize: '10px',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            backgroundColor: (() => {
                              const status = task.status?.toLowerCase();
                              if (status === 'completed') {
                                return '#16a34a'; 
                              } else if (status === 'assigned' || status === 'in progress') {
                                return '#ea580c'; 
                              } else if (status === 'completed') {
                                return '#475569'; 
                              } else {
                                return '#6b7280'; 
                              }
                            })(),
                            color: 'white',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            {task.status?.replace('_', ' ') || 'pending'}
                          </span>
                        </div>
                        {(task.due_date || task.completed_on) && (
                          <div style={{
                            fontSize: '11px',
                            color: (() => {
                              if (task.status?.toLowerCase() === 'completed' && task.completed_on) {
                                return '#16a34a'; // green for completed
                              }
                              const dueDate = new Date(task.due_date);
                              const today = new Date();
                              const isOverdue = dueDate < today;
                              const isDueSoon = dueDate <= new Date(today.getTime() + 3 * 24 * 60 * 60 * 1000); // 3 days
                              
                              if (isOverdue) return '#dc2626'; // red
                              if (isDueSoon) return '#f59e0b'; // amber
                              return '#6b7280'; // gray
                            })(),
                            fontWeight: '500'
                          }}>
                            {task.status?.toLowerCase() === 'completed' && task.completed_on 
                              ? `Completed: ${new Date(task.completed_on).toLocaleDateString()}`
                              : `Due: ${new Date(task.due_date).toLocaleDateString()}`}
                          </div>
                        )}
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: '#374151',
                        marginBottom: '4px',
                        lineHeight: '1.4'
                      }}>
                        {task.description?.length > 80 ? `${task.description.substring(0, 80)}...` : task.description || 'No description'}
                      </div>
                      {task.assigned_to && (
                        <div style={{
                          fontSize: '11px',
                          color: '#6b7280'
                        }}>
                          Assigned to: {task.assigned_to}
                        </div>
                      )}
                    </div>
                    );
                  })}
                  
                  {totalTasks > 2 && (
                    <div
                      className="task-popup-expand-button"
                      onClick={handleExpandToggle}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '6px',
                        borderTop: '1px solid #e5e7eb',
                        marginTop: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: '#6b7280',
                        backgroundColor: 'transparent',
                        border: 'none'
                      }}
                    >
                      <span style={{ marginRight: '4px' }}>
                        {taskHoverPopup.expanded ? 'Show Less' : `Show ${totalTasks - 2} More`}
                      </span>
                      <i className={`bi ${taskHoverPopup.expanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Bulk Actions Popup */}
      <BulkActionsPopup
        selectedRows={selectedRows}
        bulkActions={bulkActions}
        onClose={handleClearSelection}
        itemLabel="item"
      />

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal 
          show={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleExportModalSave}
          data={filteredExportData}
          allColumns={exportColumns}
          visibleColumns={exportColumns}
          viewName={currentView !== 'default' ? 'custom' : 'default'}
          entityName="items"
        />
      )}

      {/* Delete Item Modal */}
      <Modal
        show={deleteModal.show}
        onClose={() => setDeleteModal({ show: false, item: null })}
        title="Move Item to Trash"
        size={isMobile ? 'modal-sm' : 'modal-dialog-centered'}
      >
        <p>Are you sure you want to delete this item?</p>
        <p><strong>"{formatItemRef(deleteModal.item?.id)} - {deleteModal.item?.title}"</strong></p>
        <p>The item will be moved to the trashcan where it can be restored within 30 days before being permanently deleted.</p>
        
        <div className="modal-actions">
          <button 
            className="btn btn-secondary"
            onClick={() => setDeleteModal({ show: false, item: null })}
          >
            Cancel
          </button>
          <button 
            className="btn btn-danger"
            onClick={handleConfirmDelete}
            disabled={deleteItemMutation.isPending}
          >
            {deleteItemMutation.isPending ? 'Moving to Trash...' : 'Move to Trash'}
          </button>
        </div>
      </Modal>

      {/* Bulk Delete Confirmation Modal */}
      <Modal
        show={bulkDeleteModal.show}
        onCancel={() => setBulkDeleteModal({ show: false, selectedRows: new Set() })}
        onConfirm={handleConfirmBulkDelete}
        title="Move Items to Trash"
        size={isMobile ? 'modal-sm' : 'modal-dialog-centered'}
        confirmText={isBulkDeleting ? 'Moving to Trash...' : 'Move to Trash'}
        cancelText="Cancel"
        confirmBtnClass="btn-danger"
        loading={isBulkDeleting}
        content={
          <div>
            <p>Are you sure you want to delete {bulkDeleteModal.selectedRows.size} selected item{bulkDeleteModal.selectedRows.size !== 1 ? 's' : ''}?</p>
            <p>The item{bulkDeleteModal.selectedRows.size !== 1 ? 's' : ''} will be moved to the trashcan where {bulkDeleteModal.selectedRows.size !== 1 ? 'they' : 'it'} can be restored within 30 days before being permanently deleted.</p>
          </div>
        }
      />

      {/* Delete View Modal */}
      <Modal
        show={deleteViewModal.show}
        onCancel={() => setDeleteViewModal({ show: false, view: null })}
        onConfirm={handleConfirmDeleteView}
        title="Delete View"
        size={isMobile ? 'modal-sm' : 'modal-dialog-centered'}
        confirmText={deleteViewMutation.isPending ? 'Deleting...' : 'Delete View'}
        cancelText="Cancel"
        confirmBtnClass="btn-danger"
        loading={deleteViewMutation.isPending}
        content={
          <div>
            <p>Are you sure you want to delete this view?</p>
            <p><strong>"{deleteViewModal.view?.view_name}"</strong></p>
            <p>The item will be moved to the trashcan where it can be recovered for 30 days.</p>
          </div>
        }
      />
    </>
  );
}